import { posClient } from './client';
import {
  normalizeBranch,
  wireBranchSchema,
  wireBranchesIndexSchema,
  type Branch,
} from '@/types/branches';
import { z } from 'zod';

export interface BranchesListOptions {
  signal?: AbortSignal;
  /**
   * Include deactivated branches.
   *
   * The office switcher needs them: deactivating the branch you are standing on
   * would otherwise remove it from the only control that could move you off it,
   * leaving the user stuck on a branch they can no longer select.
   */
  includeInactive?: boolean;
}

export interface BranchCreateInput {
  name: string;
  slug: string;
  country: string;
  city: string;
  location?: string | null;
  isActive?: boolean;
  deliveryEnabled?: boolean;
  pickupEnabled?: boolean;
  disableOrdering?: boolean;
  minimumOrderValue?: string | null;
  freeDeliveryAbove?: string | null;
  schedule?: BranchSchedule | null;
}

const wireBranchCreatedSchema = z.looseObject({ data: wireBranchSchema });

export const branchesApi = {
  /**
   * GET {pos}/branches — raw Laravel paginator, `areas` eager-loaded
   * (BranchController.php:14-61). Fetch every page so access/target pickers
   * never silently omit a valid branch.
   */
  async list(options: BranchesListOptions = {}): Promise<Branch[]> {
    const branches: Branch[] = [];
    let pageNumber = 1;
    let lastPage = 1;

    do {
      const res = await posClient.get('/branches', {
        params: {
          // Omitted rather than 0: the controller filters on presence, so sending
          // is_active=0 would ask for inactive branches only.
          ...(options.includeInactive ? {} : { is_active: 1 }),
          page: pageNumber,
          per_page: 100,
        },
        signal: options.signal,
      });
      const page = wireBranchesIndexSchema.parse(res.data);
      branches.push(...page.data.map(normalizeBranch));
      lastPage = page.last_page;
      pageNumber += 1;
    } while (pageNumber <= lastPage);

    return branches;
  },

  /** Defaults match the Filament form. */
  async create(input: BranchCreateInput, options: BranchesListOptions = {}): Promise<Branch> {
    const res = await posClient.post(
      '/branches',
      {
        name: input.name,
        slug: input.slug,
        country: input.country,
        city: input.city,
        location: input.location ?? null,
        is_active: input.isActive ?? true,
        delivery_enabled: input.deliveryEnabled ?? false,
        pickup_enabled: input.pickupEnabled ?? false,
        disable_ordering: input.disableOrdering ?? false,
        minimum_order_value: input.minimumOrderValue ?? null,
        free_delivery_above: input.freeDeliveryAbove ?? null,
        ...(input.schedule ? { schedule: input.schedule } : {}),
      },
      { signal: options.signal },
    );
    return normalizeBranch(wireBranchCreatedSchema.parse(res.data).data);
  },

  /** Hard delete: no soft-delete and no FK guard, so confirm first. */
  async remove(branchId: number, options: BranchesListOptions = {}): Promise<void> {
    await posClient.delete(`/branches/${branchId}`, { signal: options.signal });
  },
};

/* ------------------------------------------ branch settings (V2-2 / V2-8) */

/**
 * A day in a branch's opening schedule.
 *
 * The shape is the backend's own: BranchController validates `schedule` as a
 * nullable array with `schedule.timezone` and `schedule.days`, and the main
 * app's OrderCreationService::validateBranchSchedule is what reads it when an
 * order is placed. Nothing is reinterpreted here — no holiday calendar, no
 * exceptions, no timezone conversion, no delivery slots.
 */
export interface BranchScheduleSlot {
  open_time: string;
  close_time: string;
}

/**
 * One weekday's hours, in the shape the reader actually uses.
 *
 * OrderCreationService::validateBranchSchedule looks up
 * `schedule.days[<lowercase weekday>]` and reads `is_open` and `slots`, where
 * each slot has `open_time` and `close_time` as HH:mm. An earlier version of
 * this console sent a LIST of `{day, open, close, closed}` instead: it stored
 * without complaint and the reader then found no key for today, so the hours
 * had no effect at all.
 */
export interface BranchScheduleDay {
  is_open: boolean;
  slots: BranchScheduleSlot[];
}

export interface BranchSchedule {
  timezone?: string | null;
  /** Keyed by lowercase weekday name — not an array. */
  days?: Record<string, BranchScheduleDay>;
  [key: string]: unknown;
}

/** The weekday keys the reader looks up, in display order. */
export const SCHEDULE_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** The full branch record the settings screen edits. */
export interface BranchSettings {
  id: number;
  name: string;
  slug: string;
  country: string;
  city: string;
  /** Free-text address line. Real column, editable. */
  location: string | null;
  minimumOrderValue: string | null;
  freeDeliveryAbove: string | null;
  isActive: boolean;
  deliveryEnabled: boolean;
  pickupEnabled: boolean;
  disableOrdering: boolean;
  schedule: BranchSchedule | null;
}

/**
 * GET {pos}/branches/{id} returns the branch RAW — `response()->json($branch)`
 * with no envelope at all, unlike almost every other POS read. Expecting
 * `{data}` or `{branch}` here meant the settings page could never load.
 *
 * Both envelopes are still tolerated so a future wrapper does not break this,
 * but the bare object is the shape that actually arrives today.
 */
const wireBranchShowSchema = z.union([
  z.looseObject({ data: wireBranchSchema }),
  z.looseObject({ branch: wireBranchSchema }),
  wireBranchSchema,
]);

function toSettings(wire: z.infer<typeof wireBranchSchema>): BranchSettings {
  return {
    id: wire.id,
    name: wire.name,
    slug: wire.slug ?? '',
    country: wire.country ?? '',
    city: wire.city ?? '',
    location: (wire.location as string | null | undefined) ?? null,
    minimumOrderValue: (wire.minimum_order_value as string | null | undefined) ?? null,
    freeDeliveryAbove: (wire.free_delivery_above as string | null | undefined) ?? null,
    isActive: wire.is_active,
    deliveryEnabled: wire.delivery_enabled ?? false,
    pickupEnabled: wire.pickup_enabled ?? false,
    disableOrdering: wire.disable_ordering ?? false,
    schedule: (wire.schedule as BranchSchedule | null) ?? null,
  };
}

export const branchSettingsApi = {
  /** GET {pos}/branches/{id}. The envelope has varied, so both shapes parse. */
  async show(branchId: number, options: BranchesListOptions = {}): Promise<BranchSettings> {
    const res = await posClient.get(`/branches/${branchId}`, { signal: options.signal });
    const body = wireBranchShowSchema.parse(res.data) as Record<string, unknown>;
    const wire = (body.data ?? body.branch ?? body) as z.infer<typeof wireBranchSchema>;

    return toSettings(wire);
  },

  /**
   * PUT {pos}/branches/{id}.
   *
   * QUIRK, isolated here rather than pushed onto callers: the endpoint
   * validates `name`, `slug`, `country` and `city` as REQUIRED even when only
   * the schedule or a fulfilment toggle is changing. Sending a partial body
   * fails validation on four fields the user never touched, so this adapter
   * always resends the whole record and takes the current values as its base.
   *
   * `country` and `city` are checked against the server's LocationDataService,
   * and city is validated against the chosen country — which is why both are
   * echoed back unchanged rather than being offered as free text.
   */
  async update(
    current: BranchSettings,
    changes: Partial<
      Pick<
        BranchSettings,
        | 'name'
        | 'slug'
        | 'country'
        | 'city'
        | 'location'
        | 'minimumOrderValue'
        | 'freeDeliveryAbove'
        | 'isActive'
        | 'deliveryEnabled'
        | 'pickupEnabled'
        | 'disableOrdering'
        | 'schedule'
      >
    >,
    options: BranchesListOptions = {},
  ): Promise<void> {
    const next = { ...current, ...changes };

    await posClient.put(
      `/branches/${current.id}`,
      {
        name: next.name,
        slug: next.slug,
        country: next.country,
        city: next.city,
        location: next.location,
        minimum_order_value: next.minimumOrderValue,
        free_delivery_above: next.freeDeliveryAbove,
        is_active: next.isActive,
        delivery_enabled: next.deliveryEnabled,
        pickup_enabled: next.pickupEnabled,
        disable_ordering: next.disableOrdering,
        schedule: next.schedule,
      },
      { signal: options.signal },
    );
  },
};
